/**
 * M7 S25: after the commit (spec §8.4, §8.2, §11). No credits: a **generated Wire epilogue** over
 * `finale.json epilogue.days` (~10) online faction days, 4–6 items, one per story flag and sim fact (the path taken,
 * what stays broken, the gate's stage, the vote and its ballots, Okafor, Reyes, Hale's deal, the player's claims),
 * each with an outlet. Dry and never celebratory (`epilogue.bannedWords`, tested).
 *
 * The **kinds** are picked at the first commit (what the story holds then) and scheduled as `aftermath` countdowns
 * spread over the days; each item's **variant** is read when it falls due (the vote may have passed by then, the
 * gate may have moved). Ladder moves get their own Wire echoes from `callbacks.json` (S11), not a second epilogue.
 *
 * `afterFinaleCommit` (the story link calls it after every successful `commitFinale`) also:
 * - schedules the **Candle-dark** hidden event `blackmail.candleDarkOnlineDays` online days after a Blackmail commit
 *   (the Watch learns; `finale.candleDarkOnlineDay` and the world flag `candle_dark`; another outlet notes the relay's silence);
 * - on **Sell**, hands the bundle's held originals to Meridian (`sold`, where `meridian`): the evidence leaves the
 *   hold for good, which is also why Sell is final (D-TBD).
 *
 * Pure (Career in, Career out); event-driven, never per frame.
 */
import type { Career } from '../../career/types'
import type { CorpFactionId, GateStageName } from '../../strategic/types'
import { scheduleCountdown, type CountdownHandler } from '../countdowns'
import { sellItem } from '../items'
import type { FinaleConfig, WirePostConfig } from '../schemas'
import { hasFlag, storyOf, withStory } from '../state'
import {
  STORY_ITEM_IDS,
  type CountdownKind,
  type FinalePath,
  type StoryCommand,
  type StoryCountdown,
  type StoryItemId,
  type StoryResult,
} from '../types'
import { finaleData, type PathMove } from './paths'

/** Epilogue item kinds, in priority order (the first `maxItems` that apply are posted). */
export const EPILOGUE_KINDS = ['path', 'broken', 'stability', 'vote', 'okafor', 'reyes', 'hale', 'claims'] as const
export type EpilogueKind = (typeof EPILOGUE_KINDS)[number]

/** The sim facts an item reads when it falls due (the link fills them from `StoryView` and the vote). */
export interface EpilogueFacts {
  stage: GateStageName
  corpFaction: CorpFactionId | null
  /** The ban vote's ballots, once counted. */
  ballots?: { for: number; against: number }
}

export const AFTERMATH = {
  epiloguePrefix: 'finale:epilogue:',
  candleDark: 'finale:candleDark',
} as const

const ITEM_IDS: ReadonlySet<string> = new Set(STORY_ITEM_IDS)

function isItemId(id: string): id is StoryItemId {
  return ITEM_IDS.has(id)
}

/** The kinds that apply to this career now (at least the four always-on ones), capped at `maxItems`. */
export function epilogueKinds(career: Career, cfg: FinaleConfig = finaleData().finale): EpilogueKind[] {
  const story = storyOf(career)
  const paths = story.finale.paths
  const applies: Record<EpilogueKind, boolean> = {
    path: paths.length > 0,
    broken: paths.length > 0,
    stability: true,
    vote: paths.includes('expose'),
    okafor: story.okaforState !== undefined,
    reyes: story.reyesState !== undefined,
    hale: hasFlag(career, 'hale_deal') || hasFlag(career, 'hale_refused') || hasFlag(career, 'hale_deal_voided'),
    claims: true,
  }
  const out = EPILOGUE_KINDS.filter((k) => applies[k])
  return out.slice(0, cfg.epilogue.maxItems)
}

/** Online-day offsets for `n` items spread over `days` (1-based, strictly increasing when days ≥ n). */
export function epilogueOffsets(n: number, days: number): number[] {
  const out: number[] = []
  for (let i = 0; i < n; i++) out.push(Math.max(i + 1, Math.round(((i + 1) * days) / (n + 1))))
  return out
}

function fill(text: string, facts: EpilogueFacts): string {
  return text.replace('{for}', String(facts.ballots?.for ?? 0)).replace('{against}', String(facts.ballots?.against ?? 0))
}

/** The line for one epilogue item, read now (variants follow the Career and the facts as they are today). */
export function epilogueItem(career: Career, kind: EpilogueKind, facts: EpilogueFacts, cfg: FinaleConfig = finaleData().finale): WirePostConfig {
  const items = cfg.epilogue.items
  const story = storyOf(career)
  const first: FinalePath = story.finale.paths[0] ?? 'expose'
  let post: WirePostConfig
  switch (kind) {
    case 'path':
      post = items.path[first]
      break
    case 'broken':
      post = items.broken[first]
      break
    case 'stability':
      post = items.stability[facts.stage]
      break
    case 'vote':
      post = items.vote[story.finale.vote ?? 'pending']
      break
    case 'okafor': {
      const o = story.okaforState
      post = o === 'aboard' || o === 'safe' || o === 'held' ? items.okafor[o] : items.okafor.absent
      break
    }
    case 'reyes':
      post = items.reyes[story.reyesState ?? 'cool']
      break
    case 'hale':
      post = hasFlag(career, 'hale_deal_voided') ? items.hale.voided : hasFlag(career, 'hale_deal') ? items.hale.deal : items.hale.refused
      break
    case 'claims':
      post = facts.corpFaction !== null ? items.claims.corp : items.claims.pilot
      break
  }
  return { ...post, headline: fill(post.headline, facts) }
}

/** The whole epilogue as it would read with these facts (the UI preview and the tests). */
export function epilogue(career: Career, facts: EpilogueFacts, cfg: FinaleConfig = finaleData().finale): WirePostConfig[] {
  return epilogueKinds(career, cfg).map((k) => epilogueItem(career, k, facts, cfg))
}

/** True when `text` uses a banned (celebratory) word, whole-word and in any case. */
export function usesBannedWord(text: string, banned: readonly string[]): boolean {
  const words = text.toLowerCase().split(/[^a-z']+/)
  return banned.some((b) => words.includes(b.toLowerCase()))
}

function wire(post: WirePostConfig): StoryCommand {
  return { type: 'postWire', headline: post.headline, severity: post.severity, source: post.source }
}

/**
 * After a successful `commitFinale` (the link calls it with the same move): the epilogue on the first commit, the
 * Candle-dark event after a Blackmail commit, the Sell bundle's originals to Meridian.
 */
export function afterFinaleCommit(career: Career, move: PathMove, cfg: FinaleConfig = finaleData().finale): StoryResult<Career> {
  let next = career
  if (move.from === undefined) {
    const kinds = epilogueKinds(next, cfg)
    const offsets = epilogueOffsets(kinds.length, cfg.epilogue.days)
    kinds.forEach((kind, i) => {
      next = scheduleCountdown(next, { id: `${AFTERMATH.epiloguePrefix}${i}`, kind: 'aftermath', inOnlineDays: offsets[i] ?? 1, ref: kind })
    })
  }
  if (move.to === 'blackmail') {
    next = scheduleCountdown(next, { id: AFTERMATH.candleDark, kind: 'aftermath', inOnlineDays: cfg.blackmail.candleDarkOnlineDays })
  }
  if (move.to === 'sell') {
    const bundle = storyOf(next).bundles.find((b) => b.path === 'sell')
    for (const e of bundle?.entries ?? []) {
      if (e.kind !== 'item' || !isItemId(e.id)) continue
      const r = sellItem(next, e.id, 'meridian')
      if (r.ok) next = r.value.career
    }
  }
  return { career: next, commands: [], effects: [] }
}

const EPILOGUE_KIND_SET: ReadonlySet<string> = new Set(EPILOGUE_KINDS)

function isEpilogueKind(v: string | undefined): v is EpilogueKind {
  return v !== undefined && EPILOGUE_KIND_SET.has(v)
}

/**
 * The `aftermath` countdown handler for `tickOnlineDay` (the link merges it with the others): posts a due epilogue
 * item with today's facts, or runs the Candle-dark event (only while Blackmail's stipend was taken: the Watch learns).
 */
export function aftermathHandlers(
  facts: () => EpilogueFacts,
  cfg: FinaleConfig = finaleData().finale,
): Partial<Record<CountdownKind, CountdownHandler>> {
  const handler: CountdownHandler = (career: Career, fired: StoryCountdown) => {
    if (fired.id === AFTERMATH.candleDark) {
      const finale = storyOf(career).finale
      if (finale.candleDarkOnlineDay !== undefined || !hasFlag(career, 'voss_blackmailed')) return { career, commands: [], effects: [] }
      const onlineDay = storyOf(career).onlineDays
      return {
        career: withStory(career, (s) => ({ ...s, finale: { ...s.finale, candleDarkOnlineDay: onlineDay } })),
        commands: [{ type: 'setWorldFlag', key: 'candle_dark', value: true }, wire(cfg.blackmail.candleDark)],
        effects: [],
      }
    }
    if (fired.id.startsWith(AFTERMATH.epiloguePrefix) && isEpilogueKind(fired.ref)) {
      return { career, commands: [wire(epilogueItem(career, fired.ref, facts(), cfg))], effects: [] }
    }
    return { career, commands: [], effects: [] }
  }
  return { aftermath: handler }
}
